// Per-user access: one level per menu row (No access / View / Full) plus three tick boxes. This is the only thing that
// grants access; the role on a user is a label (it still decides whose records a supplier or agent sees, and keeps a
// QC inspector to Ready for QC and their own inspections). The older detailed permissions (pages, actions, columns)
// are worked out from it, so every check written against them keeps working.
//   No access: not in the menu; its pages and API refused.
//   View: open and read, export where there is an export; no create, edit, approve, submit or delete.
//   Full: create, edit, approve and submit. Deleting also needs "Can delete records".
import { MENU, MENU_FOOTER, accessRows, menuLeaves, type MenuItem } from "./menu.js";
import { ACTION_DEFS, PAGE_DEFS, roleTemplate, type AccessLevel, type Permissions, type Role } from "./permissions.js";

export const LEVELS = ["none", "view", "full"] as const;
export type Level = (typeof LEVELS)[number];
export const LEVEL_LABEL: Record<Level, string> = { none: "No access", view: "View", full: "Full" };
const RANK: Record<Level, number> = { none: 0, view: 1, full: 2 };

export interface Access {
  items: Record<string, Level>;
  /** Everything was Full when saved: items added to the menu later are Full too. */
  allFull: boolean;
  /** Can see costs and prices (FOB prices, costings, margins, finance amounts). */
  costs: boolean;
  /** Can delete records (on the items with Full). */
  delete: boolean;
  /** Can see fibre composition (confidential). */
  composition: boolean;
  /** Approvals a Full row does not include, carried over from the older permissions (row → action ids). */
  except: Record<string, string[]>;
  /** Rows that are Full only for their listed actions (no general editing), carried over from people who could only
   *  open the page but had one of its actions (a supplier submitting packing lists). */
  limited?: Record<string, true>;
}

const ITEM_BY_ROW = (): Map<string, MenuItem> => new Map(menuLeaves().filter((m) => !m.sameAs).map((m) => [m.id, m]));
export const ACTION_LABEL: Record<string, string> = Object.fromEntries(ACTION_DEFS.map(([k, l]) => [k, l]));

export function blankAccess(): Access {
  return { items: Object.fromEntries(accessRows().map((r) => [r.id, "none" as Level])), allFull: false, costs: false, delete: false, composition: false, except: {} };
}
export function normalizeAccess(raw: unknown): Access {
  const a = blankAccess();
  if (!raw || typeof raw !== "object") return a;
  const r = raw as any;
  a.allFull = r.allFull === true;
  for (const row of accessRows()) {
    const v = r.items?.[row.id];
    a.items[row.id] = (LEVELS as readonly string[]).includes(v) ? v : a.allFull ? "full" : "none";
  }
  a.costs = r.costs === true; a.delete = r.delete === true; a.composition = r.composition === true;
  if (r.limited && typeof r.limited === "object") for (const k of Object.keys(r.limited)) if (r.limited[k] === true && a.items[k] === "full") (a.limited ||= {})[k] = true;
  if (r.except && typeof r.except === "object") {
    for (const [k, v] of Object.entries(r.except)) if (Array.isArray(v) && v.length && (k === "*" || a.items[k] === "full")) a.except[k] = v.map(String).filter((x) => ACTION_LABEL[x]);
  }
  return a;
}
/** Ready to store: remembers "everything Full" so later menu items are Full too. */
export function storeAccess(a: Access): Access {
  const allFull = accessRows().every((r) => a.items[r.id] === "full");
  const limited = Object.fromEntries(Object.keys(a.limited || {}).filter((k) => a.items[k] === "full").map((k) => [k, true as const]));
  return { ...a, allFull, except: Object.fromEntries(Object.entries(a.except).filter(([k, v]) => v.length && (k === "*" || a.items[k] === "full"))), ...(Object.keys(limited).length ? { limited } : { limited: undefined }) };
}
export const rowLevel = (a: Access | null | undefined, row: string): Level => (a ? a.items[row] ?? (a.allFull ? "full" : "none") : "none");
export const atLeast = (l: Level, need: Level) => RANK[l] >= RANK[need];

// ---- Starting points (a copy to begin from; changing them later never changes anybody) --------------------------------
export const STARTING_POINTS: Array<[string, string]> = [["admin", "Admin"], ["merchandiser", "Merchandiser"], ["qcinspector", "QC inspector"], ["finance", "Finance"], ["viewer", "Viewer"],
  ["supplier", "Supplier (tasks, Time & Action Plan, samples to send)"], ["agent", "Agent (supplier's view + POs and shipments)"]];
/** Suppliers see only what they act on (approved 19/10/2026): their tasks, their lines on the Time & Action Plan (no
 *  prices; notes and the comments shared with them), the samples to send and the claims sent to them. Agents: the same for every supplier they
 *  look after, plus their purchase orders and shipments to read. A new supplier or agent user starts here; an Admin can
 *  open more for one person. */
export const PARTNER_ROWS: Record<"supplier" | "agent", Record<string, Level>> = {
  // Claims (view) stays: claims are sent to the supplier and agent, who reply Agree or Dispute.
  supplier: { tasks: "full", critical: "full", dispatch: "full", financeclaims: "view" },
  agent: { tasks: "full", critical: "full", dispatch: "full", po: "view", shipmentcontrol: "view", financeclaims: "view" },
};
const FINANCE_ROWS = ["financeledgers", "financelc", "financelanded", "financereports", "logisticsdb"];
export function startingPoint(id: string): Access {
  const a = blankAccess();
  const set = (rows: string[], l: Level) => rows.forEach((r) => (a.items[r] = l));
  const all = accessRows().map((r) => r.id);
  if (id === "admin") { set(all, "full"); a.costs = a.delete = a.composition = true; return storeAccess(a); }
  if (id === "viewer") { set(all, "view"); return a; }
  if (id === "qcinspector") { set(["qc"], "full"); return a; }
  if (id === "supplier" || id === "agent") {
    for (const [row, l] of Object.entries(PARTNER_ROWS[id])) a.items[row] = l;
    // Time & Action Plan: notes only (no workflow, sample or bulk changes; columns other than Notes are read-only for
    // suppliers and agents), as they always had. No printing or uploading.
    a.except = { critical: ["editWorkflow", "updateBulk", "approveSamples", "approvePPS", "approveSTP"], tasks: ["assignTasks"], "*": ["printPdf", "uploadDocuments"] };
    return a;
  }
  if (id === "finance") {
    set(["dashboard", "po", "shipmentcontrol", "supplierdb", "agentdb"], "view");
    set(FINANCE_ROWS, "full");
    set(["financeclaims"], "full");
    a.costs = true;
    return a;
  }
  // Merchandiser: the older Merchandiser role, Full where it could create or edit, with no carried-over exceptions.
  const m = fromLegacy(roleTemplate("Merchandiser"), "Merchandiser");
  m.except = {};
  m.items.qcstandard = "full";
  m.costs = false; m.delete = false; m.composition = false;
  return m;
}

// ---- Moving the older permissions across ---------------------------------------------------------------------------------
const OLD_RANK: Record<AccessLevel, number> = { none: 0, view: 1, edit: 2, full: 3 };
/**
 * The new levels for a user's older permissions. Never more than they had:
 *  • Full when they could edit the item's page(s) (create / edit); approvals of that item they did not have are kept
 *    as exceptions on the row;
 *  • View when they could only open it; otherwise No access.
 * Costs and prices, delete and fibre composition: ticked only if they had them.
 */
export function fromLegacy(p: Permissions, role: Role): Access {
  const a = blankAccess();
  if (role === "QC inspector") { a.items.qc = "full"; return a; }
  const page = (id: string) => OLD_RANK[p.pages[id] ?? "none"];
  for (const [rowId, item] of ITEM_BY_ROW()) {
    let lvl: Level = "none";
    const pages = item.pages || [];
    if (rowId === "settings") lvl = role === "Admin" ? "full" : "none";
    // Claims (new): Full for Admins and merchandisers who could create or edit POs; View for others who could open POs.
    else if (rowId === "financeclaims") lvl = page("po") >= 2 && (role === "Admin" || role === "Merchandiser") ? "full" : page("po") >= 1 ? "view" : "none";
    else if (FINANCE_ROWS.includes(rowId)) lvl = role === "Admin" ? "full" : "none";   // Finance was for Admins only
    // The order timeline came with Purchase Orders for the organisation's own team; Admins and merchandisers could add events.
    else if (rowId === "timeline") lvl = p.dataScope.type === "all" && page("po") >= 1 ? (role === "Admin" || role === "Merchandiser" ? "full" : "view") : "none";
    else if (rowId === "activity") lvl = p.actions.viewActivity ? "view" : "none";
    else if (pages.length) {
      const min = Math.min(...pages.map(page));
      lvl = min >= 2 ? "full" : min >= 1 ? "view" : "none";
      // Could only open the page but had one of its actions (a supplier submitting packing lists): Full for those
      // actions only, never general editing.
      if (lvl === "view" && (item.actions || []).some((x) => p.actions[x])) { lvl = "full"; (a.limited ||= {})[rowId] = true; }
    }
    a.items[rowId] = lvl;
    if (lvl === "full") {
      const missing = (item.actions || []).filter((x) => !p.actions[x]);
      if (missing.length) a.except[rowId] = missing;
    }
  }
  // Print / export and uploading documents come with View / Full; users who could not before keep that (row "*").
  const globalMissing = ["printPdf", "uploadDocuments"].filter((x) => !p.actions[x]);
  if (globalMissing.length) a.except["*"] = globalMissing;
  // The QC standard: those who could change it before (Admins; QC edit with the workflow permission).
  a.items.qcstandard = role === "Admin" || (page("qc") >= 2 && p.actions.editWorkflow) ? "full" : "none";
  a.costs = (["costUsd", "amountUsd"].every((k) => p.columns.critical?.[k]?.view !== false)) && p.columns.po?.totalAmount?.view !== false && p.columns.products?.cost?.view !== false;
  a.delete = !!p.actions.deleteRecords;
  a.composition = p.columns.fabric?.composition?.view !== false;
  return a;
}

/**
 * The older detailed permissions worked out from the levels, for the checks written against them. Columns and the
 * records a supplier or agent may see stay as stored (`base`); costs and fibre composition follow the tick boxes.
 */
export function toLegacy(a: Access, role: Role, base: Permissions): Permissions {
  const p: Permissions = {
    pages: Object.fromEntries(PAGE_DEFS.map(([id]) => [id, "none" as AccessLevel])),
    actions: Object.fromEntries(ACTION_DEFS.map(([id]) => [id, false])),
    columns: JSON.parse(JSON.stringify(base.columns)),
    dataScope: { ...base.dataScope, poUids: [...base.dataScope.poUids] },
  };
  const lift = (page: string, l: AccessLevel) => { if (OLD_RANK[l] > OLD_RANK[p.pages[page] ?? "none"]) p.pages[page] = l; };
  let anyFull = false, anyOpen = false;
  for (const [rowId, item] of ITEM_BY_ROW()) {
    const l = rowLevel(a, rowId);
    if (l === "none") continue;
    anyOpen = true;
    if (l === "full") anyFull = true;
    // The order timeline reads PO data; its own changes (events, reasons) are checked on the Timeline row, so it never
    // gives more than reading Purchase Orders.
    for (const pg of item.pages || []) lift(pg, l === "full" && rowId !== "timeline" && !a.limited?.[rowId] ? "full" : "view");
    if (l === "full") for (const x of item.actions || []) if (!(a.except[rowId] || []).includes(x)) p.actions[x] = true;
    if (rowId === "activity") p.actions.viewActivity = true;
  }
  const global = a.except["*"] || [];
  p.actions.uploadDocuments = anyFull && !global.includes("uploadDocuments");
  p.actions.printPdf = anyOpen && !global.includes("printPdf");
  p.actions.deleteRecords = anyFull && a.delete;
  // Costs and prices; fibre composition.
  for (const [pg, k] of [["critical", "costUsd"], ["critical", "amountUsd"], ["po", "totalAmount"], ["products", "cost"]] as const) {
    if (p.columns[pg]?.[k]) { p.columns[pg][k].view = a.costs && p.columns[pg][k].view !== false; if (!p.columns[pg][k].view) p.columns[pg][k].edit = false; }
  }
  if (p.columns.fabric?.composition) p.columns.fabric.composition = { view: a.composition, edit: a.composition };
  // A QC inspector keeps exactly the access they had: Ready for QC, their own inspections and Help (the server allows
  // them only the inspection addresses), with no other pages or actions.
  if (role === "QC inspector") {
    for (const k of Object.keys(p.pages)) p.pages[k] = "none";
    for (const k of Object.keys(p.actions)) p.actions[k] = false;
  }
  return p;
}

// ---- Questions the app asks -------------------------------------------------------------------------------------------
/** Organisation settings, users and permissions: Full on Settings. */
export const settingsFull = (a: Access | null | undefined) => rowLevel(a, "settings") === "full";
/** Finance: any Finance row open, and costs and prices visible (Finance is all amounts). */
export const financeOpen = (a: Access | null | undefined, row?: string) => !!a && a.costs && (row ? atLeast(rowLevel(a, row), "view") : FINANCE_ROWS.some((r) => atLeast(rowLevel(a, r), "view")));
/** The first menu item a user can open: where they land after signing in. Only QC → Ready for QC. */
export function landingPath(a: Access | null | undefined): string {
  const open = [...MENU, ...MENU_FOOTER].flatMap((it) => it.children || [it]).filter((it) => !it.sameAs && atLeast(rowLevel(a, it.id), "view"));
  if (!open.length) return "/app/help";
  if (open.length === 1 && open[0].id === "qc") return "/app/qc/ready";
  return open[0].href;
}
/** Every row the same level? (for the group "Mixed" display) */
export function groupLevel(a: Access, group: string): Level | "mixed" {
  const rows = accessRows().filter((r) => r.group === group);
  const ls = [...new Set(rows.map((r) => rowLevel(a, r.id)))];
  return ls.length === 1 ? ls[0] : "mixed";
}
/** A one-line summary for lists: "Full 12 · View 3 · No access 9". */
export function accessSummary(a: Access): string {
  const n: Record<Level, number> = { full: 0, view: 0, none: 0 };
  for (const r of accessRows()) n[rowLevel(a, r.id)]++;
  return `Full ${n.full} · View ${n.view} · No access ${n.none}`;
}
/** Is `b` more than `a` anywhere? (used to prove nobody gained access when moving across) */
export function gains(before: Access, after: Access): string[] {
  const out: string[] = [];
  for (const r of accessRows()) if (RANK[rowLevel(after, r.id)] > RANK[rowLevel(before, r.id)]) out.push(r.label);
  for (const k of ["costs", "delete", "composition"] as const) if (after[k] && !before[k]) out.push(k);
  return out;
}

/** What the older permissions `after` allow that `before` did not (pages, actions, visible columns). Empty = no gain. */
export function legacyGains(before: Permissions, after: Permissions): string[] {
  const out: string[] = [];
  for (const [k, v] of Object.entries(after.pages)) if (OLD_RANK[v] > OLD_RANK[before.pages[k] ?? "none"] && !(OLD_RANK[v] === 3 && before.pages[k] === "edit")) out.push(`page ${k}: ${before.pages[k]} → ${v}`);
  for (const [k, v] of Object.entries(after.actions)) if (v && !before.actions[k]) out.push(`action ${k}`);
  for (const [pg, cols] of Object.entries(after.columns)) for (const [k, c] of Object.entries(cols)) {
    if (c.view && before.columns[pg]?.[k]?.view === false) out.push(`column ${pg}.${k} view`);
    if (c.edit && before.columns[pg]?.[k]?.edit === false) out.push(`column ${pg}.${k} edit`);
  }
  if (after.dataScope.type !== before.dataScope.type) out.push(`records ${before.dataScope.type} → ${after.dataScope.type}`);
  return out;
}

/** Settings, users and permissions (and everything an organisation Admin could do): Full on Settings, or Falcon support. */
export function isAdminActor(actor: { supportAccess?: boolean; access?: Access } | null | undefined): boolean {
  return !!actor && (!!actor.supportAccess || settingsFull(actor.access));
}
/** The level of a row for someone (Falcon support: Full everywhere). */
export function actorLevel(actor: { supportAccess?: boolean; access?: Access } | null | undefined, row: string): Level {
  if (!actor) return "none";
  if (actor.supportAccess) return "full";
  return rowLevel(actor.access, row);
}
